"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createFilterMatcher = void 0;
const condition_filter_util_js_1 = require("./condition-filter-util.js");
const filter_types_util_js_1 = require("./filter-types-util.js");
const types_js_1 = require("./types.js");
const defaultFormatter = (value) => {
    if (value === undefined) {
        return undefined;
    }
    return `${value}`;
};
const defaultDatetimeFormatter = (value) => {
    if (value === undefined) {
        return undefined;
    }
    return new Date(value).toISOString();
};
const defaultMatcher = () => false;
function createCombinedFilterMatcher(createFilterMatcherFn, filterJaql) {
    const filter = filterJaql.filter;
    if ('or' in filter) {
        return (value) => {
            return filter.or
                .map((filterItem) => createFilterMatcherFn(Object.assign(Object.assign({}, filterJaql), { filter: filterItem })))
                .reduce((acc, filterMatcher) => {
                return acc || filterMatcher(value);
            }, false);
        };
    }
    if ('and' in filter) {
        return (value) => {
            return filter.and
                .map((filterItem) => createFilterMatcherFn(Object.assign(Object.assign({}, filterJaql), { filter: filterItem })))
                .reduce((acc, filterMatcher) => {
                return acc && filterMatcher(value);
            }, true);
        };
    }
    return createFilterMatcherFn(filterJaql);
}
function createMembersFilterMatcher(filterJaql) {
    const { datatype } = filterJaql;
    const filter = filterJaql.filter;
    const formatter = datatype === 'datetime' ? defaultDatetimeFormatter : defaultFormatter;
    return (value) => {
        const formattedMembers = filter.members.map(formatter);
        const formattedValue = formatter(value);
        return formattedMembers.includes(formattedValue);
    };
}
function createExcludeMembersFilterMatcher(filterJaql) {
    const { datatype } = filterJaql;
    const filter = filterJaql.filter;
    const formatter = datatype === 'datetime' ? defaultDatetimeFormatter : defaultFormatter;
    return (value) => {
        var _a, _b;
        const formattedMembers = ((_b = (_a = filter.exclude) === null || _a === void 0 ? void 0 : _a.members) === null || _b === void 0 ? void 0 : _b.map(formatter)) || [];
        const formattedValue = formatter(value);
        return !formattedMembers.includes(formattedValue);
    };
}
function createTextFilterMatcher(filterJaql) {
    const filter = filterJaql.filter;
    const conditionFilterType = (0, condition_filter_util_js_1.getSelectedConditionOption)(filter);
    if (conditionFilterType === types_js_1.ConditionFilterType.STARTS_WITH) {
        return (value) => {
            return new RegExp(`^${filter.startsWith}`, 'i').test(value);
        };
    }
    if (conditionFilterType === types_js_1.ConditionFilterType.ENDS_WITH) {
        return (value) => {
            return new RegExp(`${filter.endsWith}$`, 'i').test(value);
        };
    }
    if (conditionFilterType === types_js_1.ConditionFilterType.CONTAINS) {
        return (value) => {
            return new RegExp(filter.contains, 'i').test(value);
        };
    }
    if (conditionFilterType === types_js_1.ConditionFilterType.EQUALS) {
        return (value) => {
            return (value.localeCompare(filter.equals, undefined, {
                sensitivity: 'base',
            }) === 0);
        };
    }
    if (conditionFilterType === types_js_1.ConditionFilterType.DOESNT_START_WITH) {
        return (value) => {
            return !new RegExp(`^${filter.doesntStartWith}`, 'i').test(value);
        };
    }
    if (conditionFilterType === types_js_1.ConditionFilterType.DOESNT_END_WITH) {
        return (value) => {
            return !new RegExp(`${filter.doesntEndWith}$`, 'i').test(value);
        };
    }
    if (conditionFilterType === types_js_1.ConditionFilterType.DOESNT_CONTAIN) {
        return (value) => {
            return !new RegExp(filter.doesntContain, 'i').test(value);
        };
    }
    if (conditionFilterType === types_js_1.ConditionFilterType.DOESNT_EQUAL) {
        return (value) => {
            return (value.localeCompare(filter.doesntEqual, undefined, {
                sensitivity: 'base',
            }) !== 0);
        };
    }
    return defaultMatcher;
}
function createNumericFilterMatcher(filterJaql) {
    const filter = filterJaql.filter;
    const conditionFilterType = (0, condition_filter_util_js_1.getSelectedConditionOption)(filter);
    if (conditionFilterType === types_js_1.ConditionFilterType.EQUALS) {
        return (value) => {
            return Number(filter.equals) === Number(value);
        };
    }
    if (conditionFilterType === types_js_1.ConditionFilterType.DOESNT_EQUAL) {
        return (value) => {
            return Number(filter.doesntEqual) !== Number(value);
        };
    }
    if (conditionFilterType === types_js_1.ConditionFilterType.LESS_THAN) {
        return (value) => {
            return Number(value) < Number(filter.toNotEqual);
        };
    }
    if (conditionFilterType === types_js_1.ConditionFilterType.GREATER_THAN) {
        return (value) => {
            return Number(value) > Number(filter.fromNotEqual);
        };
    }
    if (conditionFilterType === types_js_1.ConditionFilterType.BETWEEN) {
        return (value) => {
            return Number(filter.to) >= Number(value) && Number(value) >= Number(filter.from);
        };
    }
    if (conditionFilterType === types_js_1.ConditionFilterType.GREATER_THAN_OR_EQUAL) {
        return (value) => {
            return Number(value) >= Number(filter.from);
        };
    }
    if (conditionFilterType === types_js_1.ConditionFilterType.LESS_THAN_OR_EQUAL) {
        return (value) => {
            return Number(filter.to) >= Number(value);
        };
    }
    return defaultMatcher;
}
/** @internal */
function createFilterMatcher(filterJaql) {
    const filterJaqlInternal = filterJaql;
    const { datatype } = filterJaqlInternal;
    const filter = filterJaqlInternal.filter;
    if ((0, filter_types_util_js_1.isSpecificItemsFilter)(filter)) {
        return createMembersFilterMatcher(filterJaqlInternal);
    }
    if ((0, filter_types_util_js_1.isConditionFilter)(filter) &&
        (0, condition_filter_util_js_1.getSelectedConditionOption)(filter) === types_js_1.ConditionFilterType.IS_NOT) {
        return createExcludeMembersFilterMatcher(filterJaqlInternal);
    }
    // text filters
    if (datatype === 'text') {
        return createCombinedFilterMatcher(createTextFilterMatcher, filterJaqlInternal);
    }
    // // numeric filters
    if (datatype === 'numeric') {
        return createCombinedFilterMatcher(createNumericFilterMatcher, filterJaqlInternal);
    }
    return defaultMatcher;
}
exports.createFilterMatcher = createFilterMatcher;
